import { useState, useEffect, useMemo } from 'react';
import { useUserPermissions } from './useUserPermissions';
import { useAdmin } from './useAdmin';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

// Mapeamento de permissões por perfil
// Baseado nos perfis definidos em AccessTypePermissionsSettings
const PERMISSION_PROFILES = {
  // Administrador: todas as permissões
  administrator: [
    'org_view', 'org_edit', 'org_delete',
    'team_view', 'team_invite', 'team_manage',
    'cal_view', 'cal_manage',
    'con_view', 'con_manage',
    'inst_view', 'inst_manage',
    'flow_view', 'flow_manage',
    'chat_view_mine', 'chat_view_all', 'chat_manage',
    'prod_view', 'prod_manage',
    'page_view', 'page_manage',
    'bill_view', 'bill_manage',
    'plat_users', 'plat_store',
  ],
  // Operador: acesso limitado
  operator: [
    'con_view', 'cal_view', 'chat_view_mine', 'chat_manage',
  ],
  // Visualizador: apenas visualização
  viewer: [
    'org_view', 'team_view', 'cal_view', 'con_view', 
    'chat_view_all', 'prod_view', 'page_view',
  ],
};

// Mapeamento de roles para perfis de acesso (fallback quando access_type não está definido)
const ROLE_TO_PROFILE: Record<string, keyof typeof PERMISSION_PROFILES> = {
  'owner': 'administrator',
  'admin': 'administrator',
  'member': 'operator', // Por padrão, members são operadores
};

// Mapeamento de access_type para perfis (garantir compatibilidade)
const ACCESS_TYPE_TO_PROFILE: Record<string, keyof typeof PERMISSION_PROFILES> = {
  'administrator': 'administrator',
  'operator': 'operator',
  'viewer': 'viewer',
  'member': 'operator', // member mapeia para operator por padrão
};

// Mapeamento de rotas para permissões necessárias
export const ROUTE_PERMISSIONS: Record<string, string[]> = {
  '/chat': ['chat_view_mine', 'chat_view_all'],
  '/crm/reports': ['chat_view_all'],
  '/products': ['prod_view'],
  '/billing': ['bill_view'],
  '/settings': ['org_edit'],
  '/teams': ['team_view'],
  '/documents': ['org_view'],
  '/crm/fluitystore': ['org_view'],
};

// Mapeamento de ações para permissões necessárias
export const ACTION_PERMISSIONS: Record<string, string> = {
  'edit_organization': 'org_edit',
  'delete_organization': 'org_delete',
  'invite_member': 'team_invite',
  'manage_member': 'team_manage',
  'create_event': 'cal_manage',
  'edit_event': 'cal_manage',
  'delete_event': 'cal_manage',
  'create_contact': 'con_manage',
  'edit_contact': 'con_manage',
  'delete_contact': 'con_manage',
  'create_instance': 'inst_manage',
  'edit_instance': 'inst_manage',
  'delete_instance': 'inst_manage',
  'create_flow': 'flow_manage',
  'edit_flow': 'flow_manage',
  'delete_flow': 'flow_manage',
  'assign_chat': 'chat_manage',
  'create_product': 'prod_manage',
  'edit_product': 'prod_manage',
  'delete_product': 'prod_manage',
  'create_page': 'page_manage',
  'edit_page': 'page_manage',
  'delete_page': 'page_manage',
  'manage_subscription': 'bill_manage',
};

/**
 * Hook para verificar permissões específicas do usuário
 * Baseado no access_type do perfil OU role na organização atual
 */
export function usePermissionCheck() {
  const { role, isOwner, isAdmin: isOrgAdmin, isLoading: permissionsLoading } = useUserPermissions();
  const { isAdmin: isAdminMaster } = useAdmin();
  const { user } = useAuth();
  const [userAccessType, setUserAccessType] = useState<string | null>(null);

  // Buscar access_type do perfil do usuário
  useEffect(() => {
    const fetchAccessType = async () => {
      if (!user?.id || isAdminMaster) {
        setUserAccessType(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('access_type')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Erro ao buscar access_type:', error);
        }
        setUserAccessType(data?.access_type ?? null);
      } catch (error) {
        console.error('Erro ao buscar access_type:', error);
      }
    };

    fetchAccessType();
  }, [user?.id, isAdminMaster]);

  // AdminMaster tem todas as permissões
  const userPermissions = useMemo(() => {
    if (isAdminMaster) {
      return PERMISSION_PROFILES.administrator;
    }

    // Se o usuário é owner ou admin da organização, sempre dar permissões de administrator
    // Isso garante que owners/admins tenham acesso completo mesmo se o access_type do perfil for limitado
    if (isOwner || isOrgAdmin) {
      return PERMISSION_PROFILES.administrator;
    }

    // Prioridade: access_type do perfil > role na organização
    if (userAccessType) {
      const profile = ACCESS_TYPE_TO_PROFILE[userAccessType] || ROLE_TO_PROFILE[role || 'member'] || 'operator';
      return PERMISSION_PROFILES[profile];
    }

    // Fallback: usar role na organização
    const profile = ROLE_TO_PROFILE[role || 'member'] || 'operator';
    return PERMISSION_PROFILES[profile];
  }, [role, isAdminMaster, userAccessType, isOwner, isOrgAdmin]);

  /**
   * Verifica se o usuário tem uma permissão específica
   */
  const hasPermission = (permissionId: string): boolean => {
    if (isAdminMaster) return true;
    return userPermissions.includes(permissionId);
  };

  /**
   * Verifica se o usuário tem qualquer uma das permissões fornecidas
   */
  const hasAnyPermission = (permissionIds: string[]): boolean => {
    if (isAdminMaster) return true;
    return permissionIds.some(id => userPermissions.includes(id));
  };

  /**
   * Verifica se o usuário tem todas as permissões fornecidas
   */
  const hasAllPermissions = (permissionIds: string[]): boolean => {
    if (isAdminMaster) return true;
    return permissionIds.every(id => userPermissions.includes(id));
  };

  /**
   * Verifica se o usuário pode acessar uma rota específica
   */
  const canAccessRoute = (route: string): boolean => {
    if (isAdminMaster) return true;
    
    const requiredPermissions = ROUTE_PERMISSIONS[route];
    if (!requiredPermissions || requiredPermissions.length === 0) {
      // Se a rota não está mapeada, permitir acesso (compatibilidade)
      return true;
    }
    
    return hasAnyPermission(requiredPermissions);
  };

  /**
   * Verifica se o usuário pode executar uma ação específica
   */
  const canPerformAction = (action: string): boolean => {
    if (isAdminMaster) return true;
    
    const requiredPermission = ACTION_PERMISSIONS[action];
    if (!requiredPermission) {
      // Se a ação não está mapeada, negar acesso por segurança
      return false;
    }
    
    return hasPermission(requiredPermission);
  };

  // Enquanto as permissões (role na org) estão carregando, não mostrar "Acesso restrito" para evitar flash
  const isLoading = permissionsLoading;

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessRoute,
    canPerformAction,
    userPermissions,
    isAdminMaster,
    isLoading,
  };
}
